/**
 * <s-recipe> — turns a recipe blog post into an easy-to-follow layout.
 *
 * Required child markup:
 *   [data-recipe-body]        — the article's rich text (the merchant's post content).
 *   [data-recipe-ingredients] — an empty, hidden aside that receives the ingredients.
 *
 * On connect it moves the section that starts with an "Ingredienser"/"Ingredients"
 * heading into the aside, makes each ingredient a checkbox the baker can tick off,
 * and gives numbered step headings ("1. Saml dejen") a large step number.
 * Without JavaScript the post simply renders as written.
 */
class SRecipe extends HTMLElement {
  connectedCallback() {
    if (this.dataset.enhanced) return;
    const body = this.querySelector('[data-recipe-body]');
    const aside = this.querySelector('[data-recipe-ingredients]');
    if (!body) return;

    this.#moveIngredients(body, aside);
    this.#numberSteps(body);
    this.dataset.enhanced = 'true';
  }

  #moveIngredients(body, aside) {
    if (!aside) return;
    const root = body.firstElementChild?.matches('.richtext') ? body.firstElementChild : body;
    const headings = Array.from(root.querySelectorAll(':scope > h2, :scope > h3'));
    const heading = headings.find((el) => /^ingre[dt]i/i.test(el.textContent.trim()));
    if (!heading) return;

    const level = Number(heading.tagName.slice(1));
    const section = [heading];
    let next = heading.nextElementSibling;
    while (next) {
      const isHeading = /^H[1-6]$/.test(next.tagName);
      if (isHeading && Number(next.tagName.slice(1)) <= level) break;
      section.push(next);
      next = next.nextElementSibling;
    }

    const slot = aside.querySelector('[data-recipe-ingredients-slot]') || aside;
    section.forEach((el) => slot.append(el));

    heading.className = 'm-0 mb-4 text-2xl font-bold uppercase leading-tight text-on-surface';
    slot.querySelectorAll('ul, ol').forEach((list) => {
      list.classList.add('list-none', 'ps-0', 'm-0', 'mb-4', 'divide-y', 'divide-border');
      list.querySelectorAll(':scope > li').forEach((item) => this.#makeCheckable(item));
    });

    aside.hidden = false;
    this.dataset.ingredients = 'true';
  }

  #makeCheckable(item) {
    const label = document.createElement('label');
    label.className = 'flex cursor-pointer items-start gap-3 py-2 has-[:checked]:line-through has-[:checked]:opacity-60';
    const box = document.createElement('input');
    box.type = 'checkbox';
    box.className = 'mt-1 size-4 shrink-0 accent-moss';
    const text = document.createElement('span');
    while (item.firstChild) text.append(item.firstChild);
    label.append(box, text);
    item.append(label);
  }

  #numberSteps(body) {
    body.querySelectorAll('h2, h3').forEach((heading) => {
      const match = heading.textContent.trim().match(/^(?:Trin\s+|Step\s+)?(\d+)\s*[.)–—-]\s+(.+)$/is);
      if (!match) return;
      const number = document.createElement('span');
      number.className = 'shrink-0 text-moss';
      number.textContent = `${match[1]}.`;
      const label = document.createElement('span');
      label.textContent = match[2];
      heading.replaceChildren(number, label);
      heading.classList.add('flex', 'items-baseline', 'gap-2');
    });
  }
}

if (!customElements.get('s-recipe')) customElements.define('s-recipe', SRecipe);
